import { toNumber } from "lodash";
import { RegisterOptions } from "react-hook-form";
import { isRealDate } from "@/src/shared/utils/dateUtils";
import { 
  parseToKyiv, 
  getYear, 
  isBefore, 
  isAfter, 
  isSame,
  areSameCalendarDates,
  KYIV_TIMEZONE 
} from "./dateTime";
import { DateTime } from "luxon";
import {parseIdentCode} from "@/src/shared/utils/string";

export const MESSAGES = {
  REQUIRED: "Це поле є обов'язковим",
  PATTERN: "Значення не відповідає заданому шаблону.",
  INVALID_IDENT_CODE_WITH_BIRTH_DATE:
    "Ідентифікаційний номер не співпадає з датою народження, будь ласка, перевірте внесені дані.",
  INVALID_IDENT_CODE: "Ідентифікаційний номер є некоректним",
  MIN_LENGTH: (value: number) => `Мінімальна кількість символів — ${value}`,
  MAX_LENGTH: (value: number) => `Максимальна кількість символів — ${value}`,
  MIN_NUMBER: (value: number) => `Мінімальне значення — ${value}`,
  MAX_NUMBER: (value: number) => `Максимальне значення — ${value}`,
  MIN_MAX_NUMBER: () => `Не може бути меншим за мінімальне значення`,
  MAX_MIN_NUMBER: () => `Не може бути більшим за максимальне значення`,
};

type ValidationInputType =
  | "TextInput"
  | "NumberInput"
  | "RatingInput"
  | "RadioButtons"
  | "Checkbox"
  | "SelectInput"
  | "UserSelect"
  | "RadioCard"
  | "DatePicker";
export const validationRules: Record<
  ValidationInputType,
  (
    props: Record<string, any>,
  ) => Omit<
    RegisterOptions<any, any>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >
> = {
  TextInput: (props: Record<string, any>) => {
    const {
      minLength,
      maxLength,
      required,
      readOnly,
      pattern,
      patternErrorMessage,
      disabled,
      deps,
      checkIPN = false,
      minNumber,
      maxNumber,
      minRelativeFieldName,
      minRelativeFieldErrorMessage,
      maxRelativeFieldName,
      maxRelativeFieldErrorMessage,
    } = props;

    return {
      deps: deps,
      disabled,
      readOnly,
      minLength: minLength
        ? {
            value: minLength,
            message: MESSAGES.MIN_LENGTH(toNumber(minLength)),
          }
        : undefined,
      maxLength: maxLength
        ? {
            value: maxLength,
            message: MESSAGES.MAX_LENGTH(toNumber(maxLength)),
          }
        : undefined,
      required: required
        ? { value: true, message: MESSAGES.REQUIRED }
        : undefined,
      pattern: pattern
        ? {
            value: new RegExp(pattern),
            message: patternErrorMessage || MESSAGES.PATTERN,
          }
        : undefined,
      validate: {
        isValidIPN: (value) => {
          const isRequired = checkIPN || required;
          if (
            !pattern ||
            new RegExp(pattern).source !== new RegExp("^\\d{10}$").source ||
            !isRequired
          ) {
            return true;
          }
          if (checkIPNValidity(value)) {
            return true;
          }
          return MESSAGES.INVALID_IDENT_CODE;
        },
        min: (value: string) => {
          if (!minNumber || !value) {
            return true;
          }
          const numberValue = parseFloat(value);
          if (isNaN(numberValue) || numberValue < minNumber) {
            return MESSAGES.MIN_NUMBER(toNumber(minNumber));
          }
        },
        max: (value: string) => {
            if (!maxNumber || !value) {
                return true;
            }
            const numberValue = parseFloat(value);
            if (isNaN(numberValue) || numberValue > maxNumber) {
                return MESSAGES.MAX_NUMBER(toNumber(maxNumber));
            }
        },
        minMax: (value: string, formValues: any) => {
          if (!minRelativeFieldName || !value) {
            return true;
          }
          const minValue = formValues[minRelativeFieldName];
          const numberValue = parseFloat(value);

          if (isNaN(numberValue)) {
            return true;
          }

          if (minValue && numberValue < parseFloat(minValue)) {
            return minRelativeFieldErrorMessage || MESSAGES.MIN_MAX_NUMBER();
          }
        },
        maxMin: (value: string, formValues: any) => {
            if (!maxRelativeFieldName || !value) {
                return true;
            }
            const maxValue = formValues[maxRelativeFieldName];
            const numberValue = parseFloat(value);

            if (isNaN(numberValue)) {
                return true;
            }

            if (maxValue && numberValue > parseFloat(maxValue)) {
                return maxRelativeFieldErrorMessage || MESSAGES.MAX_MIN_NUMBER();
            }
        }
      },
      setValueAs: (value: any) =>
        typeof value === "string" ? value?.trim() : value,
    };
  },
  NumberInput: (props: Record<string, any>) => {
    const { required, disabled, deps, readOnly, minNumber, maxNumber } = props;
    return {
      deps,
      readOnly,
      disabled,
      required: required
        ? { value: true, message: MESSAGES.REQUIRED }
        : undefined,
      min: minNumber
        ? {
            value: minNumber,
            message: MESSAGES.MIN_NUMBER(toNumber(minNumber)),
          }
        : undefined,
      max: maxNumber
        ? {
            value: maxNumber,
            message: MESSAGES.MAX_NUMBER(toNumber(maxNumber)),
          }
        : undefined,
    };
  },
  RatingInput: (props: Record<string, any>) => {
    const { required, disabled, readOnly, deps } = props;
    return {
      deps,
      disabled,
      readOnly,
      required: required
        ? { value: true, message: MESSAGES.REQUIRED }
        : undefined,
    };
  },
  RadioButtons: (props: Record<string, any>) => {
    const { required, disabled, readOnly, deps } = props;
    return {
      deps,
      disabled,
      readOnly,
      required: required
        ? { value: true, message: MESSAGES.REQUIRED }
        : undefined,
    };
  },
  Checkbox: (props: Record<string, any>) => {
    const { required, disabled, readOnly, deps } = props;
    return {
      deps,
      disabled,
      readOnly,
      required: required
        ? { value: true, message: MESSAGES.REQUIRED }
        : undefined,
    };
  },
  SelectInput: (props: Record<string, any>) => {
    const { required, disabled, readOnly, deps } = props;
    return {
      deps,
      disabled,
      readOnly,
      required: required
        ? { value: true, message: MESSAGES.REQUIRED }
        : undefined,
    };
  },
  UserSelect: (props: Record<string, any>) => {
    const { required, disabled, readOnly, deps } = props;
    return {
      deps,
      disabled,
      readOnly,
      required: required
        ? { value: true, message: MESSAGES.REQUIRED }
        : undefined,
    };
  },
  RadioCard: (props: Record<string, any>) => {
    const { required, disabled, readOnly, deps } = props;
    return {
      deps,
      disabled,
      readOnly,
      required: required
        ? { value: true, message: MESSAGES.REQUIRED }
        : undefined,
    };
  },
  DatePicker: (props: Record<string, any>) => {
    const {
      required,
      disabled,
      readOnly,
      deps,
      min,
      max,
      minErrorMessage,
      maxErrorMessage,
      errorMessage,
      ipnFieldName,
      isYear,
    } = props;

    return {
      deps,
      disabled,
      readOnly,
      required: required
        ? { value: true, message: MESSAGES.REQUIRED }
        : undefined,
      validate: {
        minDate: (value: string) => {
          if (min && value) {
            const minDaysDate = parseToKyiv(min);
            if (isYear) {
              const valueYear = getYear(value);
              const minYear = minDaysDate?.year;
              
              if (valueYear !== null && minYear !== undefined && valueYear < minYear) {
                return minErrorMessage || "";
              }
            } else {
              if (isBefore(value, min, 'days')) {
                return minErrorMessage || "";
              }
            }
          }
          return true;
        },
        maxDate: (value: string) => {
          if (max && value) {
            const maxDaysDate = parseToKyiv(max);
            if (isYear) {
              const valueYear = getYear(value);
              const maxYear = maxDaysDate?.year;
              
              if (valueYear !== null && maxYear !== undefined && valueYear > maxYear) {
                return maxErrorMessage || "";
              }
            } else {
              if (isAfter(value, max, 'days')) {
                return maxErrorMessage || "";
              }
            }
          }
          return true;
        },
        invalidDate: (value: string) => {
          if (required && !isRealDate(value)) {
            return errorMessage?.length || "Некоректна дата";
          }
          return true;
        },
        isIpn: (selectedDate: string, formValues: any) => {
          if (
            selectedDate &&
            ipnFieldName &&
            parseToKyiv(selectedDate)?.isValid
          ) {
            const birthDate = parseToKyiv(selectedDate);
            const decodedIPNDate = parseIdentCode(formValues[ipnFieldName]);
            if (!decodedIPNDate || !birthDate) {
              return true;
            }

            // Use the specialized helper function that compares calendar dates
            // instead of exact DateTime objects, avoiding timezone issues
            const IPNDate = DateTime.fromISO(decodedIPNDate, { zone: KYIV_TIMEZONE });
            const datesMatch = isSame(birthDate, IPNDate);

            if (!datesMatch) {
              return MESSAGES.INVALID_IDENT_CODE_WITH_BIRTH_DATE;
            }
          }
          return true;
        },
      },
    };
  },
};

const checkIPNValidity = (ipn: string) => {
  var input = ipn || "";
  var isValid = true;
  if (input.length === 10) {
    for (var i = 0; i < input.length; i++) {
      if (input[i] < "0" || input[i] > "9") {
        isValid = false;
        break;
      }
    }

    if (isValid) {
      var digits = input.split("");
      var weights = [-1, 5, 7, 9, 4, 6, 10, 5, 7];
      var checksum = digits.slice(0, 9).reduce(function (sum, digit, i) {
        return sum + parseInt(digit, 10) * weights[i];
      }, 0);

      var controlNumber = (checksum % 11) % 10;

      if (controlNumber !== parseInt(digits[9], 10)) {
        isValid = false;
      }
    }
  } else {
    isValid = false;
  }

  return isValid;
};
